import type { DocumentPipelineFilter } from '@kanbu/schema/contracts';
import { DocumentStatus, WebsitePageStatus } from '@kanbu/schema/enums';
import { useLingui } from '@lingui/react/macro';
import { useMemo } from 'react';

import { useEnumTranslations } from '@/hooks';

import {
  KB_TRACKED_PAGE_FILTER,
  type KBWebsitePageFilter,
} from '../lib/pageFilter';

export type KBReadinessStatId = DocumentStatus;

export type KBWebsiteStatId = 'tracked' | WebsitePageStatus | 'discovered';

export type KBReadinessStatDefinition = {
  id: KBReadinessStatId;
  title: string;
  description?: string;
  pipelineFilter?: DocumentPipelineFilter;
  isError?: boolean;
  getCount: (stats: KBProcessingStats) => number;
};

export type KBWebsiteStatDefinition = {
  id: KBWebsiteStatId;
  title: string;
  description?: string;
  statusFilter?: KBWebsitePageFilter;
  isTotal?: boolean;
  getCount: (stats: KBProcessingStats) => number;
};

export type KBProcessingStats = {
  total: number;
  parsingError: number;
  embeddingError: number;
  uploading: number;
  ready: number;
  parsing: number;
  parsingAsync: number;
  parsed: number;
  embedding: number;
  done: number;
  totalPages: number;
  pagesWithDocument: number;
  newPages: number;
  existingPages: number;
  removedPages: number;
  untrackedPages: number;
};

export function useKBProcessingStatDefinitions() {
  const { t } = useLingui();
  const enumTranslations = useEnumTranslations();

  return useMemo(() => {
    const readinessStats: KBReadinessStatDefinition[] = [
      {
        id: DocumentStatus.Done,
        title: enumTranslations.documentStatus[DocumentStatus.Done],
        description: t`Successfully processed and available in the knowledge base.`,
        pipelineFilter: 'done',
        getCount: s => s.done,
      },
      {
        id: DocumentStatus.Uploading,
        title: enumTranslations.documentStatus[DocumentStatus.Uploading],
        description: t`File upload to the server is still in progress.`,
        pipelineFilter: 'processing',
        getCount: s => s.uploading,
      },
      {
        id: DocumentStatus.Ready,
        title: enumTranslations.documentStatus[DocumentStatus.Ready],
        description: t`Upload finished; processing starts soon automatically and should be finished in the next few minutes.`,
        pipelineFilter: 'processing',
        getCount: s => s.ready,
      },
      {
        id: DocumentStatus.Parsing,
        title: enumTranslations.documentStatus[DocumentStatus.Parsing],
        description: t`Reading the file and extracting content for the knowledge base.`,
        pipelineFilter: 'parsing',
        getCount: s => s.parsing + s.parsingAsync,
      },
      {
        id: DocumentStatus.Parsed,
        title: enumTranslations.documentStatus[DocumentStatus.Parsed],
        description: t`Content extracted; preparing search embeddings next.`,
        pipelineFilter: 'processing',
        getCount: s => s.parsed,
      },
      {
        id: DocumentStatus.Embedding,
        title: enumTranslations.documentStatus[DocumentStatus.Embedding],
        description: t`Indexing content so the agent can search and use it in answers.`,
        pipelineFilter: 'embedding',
        getCount: s => s.embedding,
      },
      {
        id: DocumentStatus.ParsingError,
        title: enumTranslations.documentStatus[DocumentStatus.ParsingError],
        description: t`Could not read or process the file. Try again or re-upload.`,
        pipelineFilter: 'parsingError',
        isError: true,
        getCount: s => s.parsingError,
      },
      {
        id: DocumentStatus.EmbeddingError,
        title: enumTranslations.documentStatus[DocumentStatus.EmbeddingError],
        description: t`File was read but could not be indexed for search. Try retrying.`,
        pipelineFilter: 'embeddingError',
        isError: true,
        getCount: s => s.embeddingError,
      },
    ];

    const websiteStats: KBWebsiteStatDefinition[] = [
      {
        id: 'discovered',
        title: t`Pages discovered`,
        description: t`All pages found on connected websites (includes untracked pages).`,
        isTotal: true,
        getCount: s => s.totalPages,
      },
      {
        id: 'tracked',
        title: t`Tracked`,
        description: t`Website pages added to the knowledge base.`,
        statusFilter: KB_TRACKED_PAGE_FILTER,
        getCount: s => s.pagesWithDocument,
      },
      {
        id: WebsitePageStatus.New,
        title: enumTranslations.websitePageStatus[WebsitePageStatus.New],
        description: t`Newly discovered on the website and not yet added to the knowledge base. Might need review before adding.`,
        statusFilter: WebsitePageStatus.New,
        getCount: s => s.newPages,
      },
      {
        id: WebsitePageStatus.Removed,
        title: enumTranslations.websitePageStatus[WebsitePageStatus.Removed],
        description: t`Included in knowledge base but no longer found on the website. Might need review before removing.`,
        statusFilter: WebsitePageStatus.Removed,
        getCount: s => s.removedPages,
      },
      {
        id: WebsitePageStatus.Untracked,
        title: enumTranslations.websitePageStatus[WebsitePageStatus.Untracked],
        description: t`Discovered on the website but deliberately not included in the knowledge base.`,
        statusFilter: WebsitePageStatus.Untracked,
        getCount: s => s.untrackedPages,
      },
    ];

    return { readinessStats, websiteStats };
  }, [enumTranslations, t]);
}

export function useDocumentPipelineFilterLabel() {
  const { t } = useLingui();
  const enumTranslations = useEnumTranslations();

  return useMemo(
    () =>
      ({
        processing: t`Processing`,
        errored: t`Failed`,
        done: enumTranslations.documentStatus[DocumentStatus.Done],
        parsing: enumTranslations.documentStatus[DocumentStatus.Parsing],
        embedding: enumTranslations.documentStatus[DocumentStatus.Embedding],
        parsingError:
          enumTranslations.documentStatus[DocumentStatus.ParsingError],
        embeddingError:
          enumTranslations.documentStatus[DocumentStatus.EmbeddingError],
      }) satisfies Record<DocumentPipelineFilter, string>,
    [enumTranslations, t],
  );
}
